/**
 * Types for Prompt Management feature.
 * Used by Configuration pages for admin prompt management.
 */

// ============================================================================
// Constants
// ============================================================================

/**
 * Prompt status options for dropdowns and selectors.
 */
export const PROMPT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'experimental', label: 'Experimental' },
  { value: 'deprecated', label: 'Deprecated' },
] as const

/**
 * Agent options for dropdowns and selectors.
 */
export const AGENT_OPTIONS = [
  { value: 'Planner', label: 'Planner' },
  { value: 'Reviewer', label: 'Reviewer' },
  { value: 'Coder', label: 'Coder' },
  { value: 'Chatbot', label: 'Chatbot' },
  { value: 'Scout', label: 'Scout' },
  { value: 'Orchestrator', label: 'Orchestrator' },
] as const

/**
 * Category options for dropdowns and selectors.
 */
export const CATEGORY_OPTIONS = [
  { value: 'agent-instructions', label: 'Agent Instructions' },
  { value: 'system-prompt', label: 'System Prompt' },
  { value: 'workflow', label: 'Workflow' },
] as const

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Prompt status type derived from options.
 */
export type PromptStatus = typeof PROMPT_STATUS_OPTIONS[number]['value']

/**
 * Agent type derived from options.
 */
export type Agent = typeof AGENT_OPTIONS[number]['value']

/**
 * Category type derived from options.
 */
export type Category = typeof CATEGORY_OPTIONS[number]['value']

// ============================================================================
// Data Interfaces
// ============================================================================

/**
 * Prompt metadata for categorization and search.
 */
export interface PromptMetadata {
  /** Agent this prompt is used by */
  agent: string
  /** Category of the prompt */
  category: string
  /** Estimated token count */
  tokenEstimate: number | null
  /** SHA256 hash of content */
  contentHash: string | null
}

/**
 * Summary prompt info for list display.
 */
export interface PromptSummary {
  /** Unique prompt identifier */
  promptName: string
  /** Current version number */
  version: number
  /** Current status */
  status: PromptStatus
  /** Agent this prompt is used by */
  agent: string
  /** Category of the prompt */
  category: string
  /** Last update timestamp (ISO 8601) */
  updatedAt: string
  /** Who last updated */
  updatedBy: string
}

/**
 * Version history entry.
 */
export interface PromptVersion {
  /** Version number */
  version: number
  /** Version status */
  status: PromptStatus
  /** Full prompt content */
  content: string
  /** Change description */
  changeNote: string | null
  /** When version was created (ISO 8601) */
  createdAt: string
  /** Who created this version */
  createdBy: string
  /** When version was activated (ISO 8601) */
  activatedAt: string | null
  /** Who activated this version */
  activatedBy: string | null
}

/**
 * Audit trail record for prompt state changes.
 */
export interface PromptAuditRecord {
  /** Action type: activated, deactivated, rolled_back, experiment_set, experiment_cleared */
  action: string
  /** Who performed the action */
  performedBy: string
  /** When action was performed (ISO 8601) */
  performedAt: string
  /** Previous version (for rollback/activation) */
  fromVersion: number | null
  /** New version (for activation) */
  toVersion: number | null
  /** Experiment percentage (for experiment_set) */
  experimentPercentage: number | null
  /** Optional reason for the action */
  reason: string | null
}

/**
 * Detailed prompt info with version history and audit trail.
 */
export interface PromptDetail {
  /** Unique prompt identifier */
  promptName: string
  /** Current active version number */
  currentVersion: number
  /** Current status */
  status: PromptStatus
  /** Agent this prompt is used by */
  agent: string
  /** Category of the prompt */
  category: string
  /** Full prompt content */
  content: string
  /** Estimated token count */
  tokenEstimate: number | null
  /** SHA256 hash of content */
  contentHash: string | null
  /** Experimental routing percentage (0-100) */
  experimentalPercentage: number | null
  /** Version being used for experiment */
  experimentalVersion: number | null
  /** When prompt was first created (ISO 8601) */
  createdAt: string
  /** Who created the prompt */
  createdBy: string
  /** Last update timestamp (ISO 8601) */
  updatedAt: string
  /** Who last updated */
  updatedBy: string
  /** All versions of this prompt */
  versions: PromptVersion[]
  /** Audit trail */
  auditTrail: PromptAuditRecord[]
}

// ============================================================================
// Request Types
// ============================================================================

/**
 * Request to create a new prompt.
 */
export interface CreatePromptRequest {
  /** Unique prompt name (slug format) */
  promptName: string
  /** Initial prompt content */
  content: string
  /** Agent this prompt is for */
  agent: string
  /** Category of the prompt */
  category: string
  /** Description of initial version */
  changeNote?: string
}

/**
 * Request to create a new version of an existing prompt.
 */
export interface CreateVersionRequest {
  /** New prompt content */
  content: string
  /** Description of changes */
  changeNote?: string
}

/**
 * Request to activate a specific version.
 */
export interface ActivateVersionRequest {
  /** Optional reason for activation */
  reason?: string
}

/**
 * Request to set experimental routing percentage.
 */
export interface SetExperimentalRequest {
  /** Percentage of traffic to route to experimental version (0-100) */
  percentage: number
}

/**
 * Request to rollback to previous version.
 */
export interface RollbackRequest {
  /** Optional reason for rollback */
  reason?: string
}

// ============================================================================
// Response Types
// ============================================================================

/**
 * Response for list prompts endpoint.
 */
export interface PromptListResponse {
  /** List of prompt summaries */
  prompts: PromptSummary[]
  /** Total count for pagination */
  total: number
}

/**
 * Diff response for comparing versions.
 */
export interface PromptDiffResponse {
  /** Prompt name */
  promptName: string
  /** From version number */
  fromVersion: number
  /** To version number */
  toVersion: number
  /** Unified diff string */
  diff: string
}
